import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Download, Check, ChevronDown, ChevronUp,
  Building2, Globe, DollarSign, Clock,
  AlertTriangle, TrendingUp, Percent, Mail, Lock, CheckCircle
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════
   PRODUCT COST MODELS
   Each product has transparent, editable parameters
   with smart defaults and clear calculation logic.
   ═══════════════════════════════════════════ */

const PRODUCT_MODELS = {
  einvoicing: {
    id: 'einvoicing', name: 'e-Invoicing', category: 'core',
    desc: 'Electronic invoice generation, validation & submission across CTC/clearance models',
    cycleLabel: 'Invoice processing cycles',
    cycleFormula: (e, c) => e * c * 12,
    cycleExplain: (e, c) => `${e} entities × ${c} countries × 12 months`,
    currentLabel: 'Manual Invoice Processing',
    futureLabel: 'Taxera e-Invoicing Automation',
    defaults: { currentHours: 6, futureHours: 0.5, errorRate: 4, costPerError: 1500, errorReduction: 95 },
    solution: { license: 35000, perCountry: 8000, impl: 20000 },
  },
  saft: {
    id: 'saft', name: 'SAF-T', category: 'core',
    desc: 'Standard Audit File — automated extraction, transformation & authority submission',
    cycleLabel: 'SAF-T submissions',
    cycleFormula: (e, c) => e * c * 4,
    cycleExplain: (e, c) => `${e} entities × ${c} countries × 4 quarters`,
    currentLabel: 'Manual SAF-T Preparation',
    futureLabel: 'Taxera SAF-T Automation',
    defaults: { currentHours: 12, futureHours: 1, errorRate: 8, costPerError: 3000, errorReduction: 92 },
    solution: { license: 30000, perCountry: 7000, impl: 18000 },
  },
  'vat-returns': {
    id: 'vat-returns', name: 'VAT Returns', category: 'core',
    desc: 'End-to-end VAT return preparation, validation & filing across jurisdictions',
    cycleLabel: 'VAT return filings',
    cycleFormula: (e, c) => e * c * 6,
    cycleExplain: (e, c) => `${e} entities × ${c} countries × 6 avg filings/year`,
    currentLabel: 'Manual VAT Return Preparation',
    futureLabel: 'Taxera VAT Returns Automation',
    defaults: { currentHours: 10, futureHours: 1, errorRate: 3, costPerError: 2500, errorReduction: 90 },
    solution: { license: 25000, perCountry: 5000, impl: 15000 },
  },
  'vat-groups': {
    id: 'vat-groups', name: 'VAT Groups', category: 'core',
    desc: 'Consolidated VAT group reporting with inter-company transaction management',
    cycleLabel: 'VAT group consolidations',
    cycleFormula: (e) => Math.ceil(e / 3) * 12,
    cycleExplain: (e) => `~${Math.ceil(e / 3)} VAT groups × 12 months`,
    currentLabel: 'Manual Consolidation',
    futureLabel: 'Taxera VAT Groups Automation',
    defaults: { currentHours: 15, futureHours: 2, errorRate: 5, costPerError: 2000, errorReduction: 88 },
    solution: { license: 20000, perCountry: 4500, impl: 12000 },
  },
  'ec-sales-list': {
    id: 'ec-sales-list', name: 'EC Sales List', category: 'core',
    desc: 'Automated EC Sales List generation and submission for intra-community supplies',
    cycleLabel: 'ECSL submissions',
    cycleFormula: (e, c) => Math.ceil(e * 0.6) * c * 4,
    cycleExplain: (e, c) => `~${Math.ceil(e * 0.6)} EU entities × ${c} countries × 4 quarters`,
    currentLabel: 'Manual ECSL Preparation',
    futureLabel: 'Taxera ECSL Automation',
    defaults: { currentHours: 4, futureHours: 0.25, errorRate: 6, costPerError: 1000, errorReduction: 94 },
    solution: { license: 15000, perCountry: 3500, impl: 8000 },
  },
  intrastat: {
    id: 'intrastat', name: 'Intrastat', category: 'core',
    desc: 'Intrastat reporting for goods movements across EU member states',
    cycleLabel: 'Intrastat reports',
    cycleFormula: (e, c) => Math.ceil(e * 0.5) * c * 12,
    cycleExplain: (e, c) => `~${Math.ceil(e * 0.5)} trading entities × ${c} countries × 12 months`,
    currentLabel: 'Manual Intrastat Reporting',
    futureLabel: 'Taxera Intrastat Automation',
    defaults: { currentHours: 5, futureHours: 0.5, errorRate: 4, costPerError: 1200, errorReduction: 91 },
    solution: { license: 18000, perCountry: 4000, impl: 10000 },
  },
  ecompliance: {
    id: 'ecompliance', name: 'eCompliance', category: 'core',
    desc: 'Holistic compliance monitoring, audit trail & real-time status tracking',
    cycleLabel: 'Compliance monitoring cycles',
    cycleFormula: (e, c) => e * c * 12,
    cycleExplain: (e, c) => `${e} entities × ${c} countries × 12 months`,
    currentLabel: 'Manual Compliance Tracking',
    futureLabel: 'Taxera eCompliance Platform',
    defaults: { currentHours: 3, futureHours: 0.25, errorRate: 2, costPerError: 5000, errorReduction: 85 },
    solution: { license: 22000, perCountry: 5000, impl: 12000 },
  },
  'vat-ecsl-recon': {
    id: 'vat-ecsl-recon', name: 'VAT to ECSL Reconciliation', category: 'recon',
    desc: 'Automated reconciliation between VAT returns and EC Sales List filings',
    cycleLabel: 'Reconciliation cycles',
    cycleFormula: (e, c) => e * c * 12,
    cycleExplain: (e, c) => `${e} entities × ${c} countries × 12 months`,
    currentLabel: 'Manual Reconciliation',
    futureLabel: 'Taxera Automated Reconciliation',
    defaults: { currentHours: 8, futureHours: 0.017, errorRate: 5, costPerError: 2000, errorReduction: 90 },
    solution: { license: 12000, perCountry: 3000, impl: 8000 },
  },
  'monthly-recon': {
    id: 'monthly-recon', name: 'Monthly Reconciliation', category: 'recon',
    desc: 'End-to-end: e-Invoicing → VAT Return → General Ledger reconciliation',
    cycleLabel: 'Monthly reconciliation cycles',
    cycleFormula: (e) => e * 12,
    cycleExplain: (e) => `${e} entities × 12 months`,
    currentLabel: 'Manual Monthly Close Reconciliation',
    futureLabel: 'Taxera Automated Reconciliation',
    defaults: { currentHours: 10, futureHours: 0.5, errorRate: 4, costPerError: 2500, errorReduction: 93 },
    solution: { license: 15000, perCountry: 3500, impl: 10000 },
  },
};

const PRODUCT_LIST = Object.values(PRODUCT_MODELS);
// Pre-compute category buckets at module level so the JSX render doesn't
// re-filter on every keystroke.
const CORE_PRODUCTS = PRODUCT_LIST.filter(p => p.category === 'core');
const RECON_PRODUCTS = PRODUCT_LIST.filter(p => p.category === 'recon');
const BLOCKED_DOMAINS = ['mailinator.com', 'tempmail.com', 'guerrillamail.com', 'throwaway.email', 'yopmail.com'];

/* ═══ Formatting ═══ */
const fmt = (n) => '€' + Math.round(n).toLocaleString('en-US');
const fmtHrs = (n) => n < 1 ? `${(n * 60).toFixed(0)} min` : n.toLocaleString('en-US', { maximumFractionDigits: 1 }) + 'h';

/* ═══════════════════════════════════════════
   HELPER COMPONENTS
   ═══════════════════════════════════════════ */

const NumField = ({ label, value, onChange, step = 1, min = 0, hint, prefix, suffix, testId }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">{prefix}</span>}
      <input
        type="number"
        step={step}
        min={min}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className={`w-full ${prefix ? 'pl-8' : 'pl-3'} ${suffix ? 'pr-10' : 'pr-3'} py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20 bg-white`}
        data-testid={testId}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">{suffix}</span>}
    </div>
    {hint && <p className="text-2xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

const MetricRow = ({ label, sublabel, value, bold, color }) => (
  <div className={`flex justify-between items-start py-2 ${bold ? 'font-bold' : ''}`}>
    <div className="min-w-0">
      <div className={`text-sm ${bold ? 'text-slate-900' : 'text-slate-700'}`}>{label}</div>
      {sublabel && <div className="text-2xs text-slate-400">{sublabel}</div>}
    </div>
    <span className={`text-sm font-semibold whitespace-nowrap ml-4 ${color || (bold ? 'text-slate-900' : 'text-slate-600')}`}>{value}</span>
  </div>
);

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */

const BusinessCasePage = () => {
  /* ─── State ─── */
  const [scope, setScope] = useState({
    clientName: '', legalEntities: 10, countries: 5, hourlyRate: 75, includeErrorCosts: true,
  });
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [productInputs, setProductInputs] = useState({});
  const [solutionOverrides, setSolutionOverrides] = useState({});
  const [sharedCosts, setSharedCosts] = useState({ trainingCost: 15000 });
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [showLeadGate, setShowLeadGate] = useState(false);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadName, setLeadName] = useState('');
  const [leadError, setLeadError] = useState('');
  const [downloadReady, setDownloadReady] = useState(false);

  /* ─── Handlers ─── */
  const updateScope = (key, val) => setScope(p => ({ ...p, [key]: key === 'clientName' ? val : (parseFloat(val) || 0) }));

  const toggleProduct = (id) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); }
      else {
        next.add(id);
        if (!productInputs[id]) {
          setProductInputs(p => ({ ...p, [id]: { ...PRODUCT_MODELS[id].defaults } }));
        }
        setExpandedProducts(e => new Set([...e, id]));
      }
      return next;
    });
  };

  const updateProductInput = (pid, key, val) => {
    setProductInputs(p => ({ ...p, [pid]: { ...p[pid], [key]: parseFloat(val) || 0 } }));
  };

  const updateSolutionOverride = (pid, key, val) => {
    setSolutionOverrides(p => ({ ...p, [pid]: { ...(p[pid] || {}), [key]: parseFloat(val) || 0 } }));
  };

  const toggleExpand = (id) => {
    setExpandedProducts(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  /* ─── Calculations (fully transparent) ─── */
  const calc = useMemo(() => {
    const perProduct = {};
    let tCurrCost = 0, tCurrHrs = 0, tCurrErrCost = 0;
    let tFutLabor = 0, tFutErrCost = 0, tFutHrs = 0;
    let tLicense = 0, tImpl = 0, tMaint = 0;

    for (const id of selectedProducts) {
      const m = PRODUCT_MODELS[id];
      const inp = productInputs[id] || m.defaults;
      const ov = solutionOverrides[id] || {};

      const cycles = m.cycleFormula(scope.legalEntities, scope.countries);
      const currHrs = cycles * inp.currentHours;
      const currLabor = currHrs * scope.hourlyRate;
      const currErrs = cycles * (inp.errorRate / 100);
      const currErrCost = scope.includeErrorCosts ? currErrs * inp.costPerError : 0;
      const currTotal = currLabor + currErrCost;

      const futHrs = cycles * inp.futureHours;
      const futLabor = futHrs * scope.hourlyRate;
      const futErrs = currErrs * (1 - inp.errorReduction / 100);
      const futErrCost = scope.includeErrorCosts ? futErrs * inp.costPerError : 0;

      const license = ov.license ?? (m.solution.license + m.solution.perCountry * scope.countries);
      const impl = ov.impl ?? m.solution.impl;
      const maintenance = ov.maintenance ?? Math.round(license * 0.2);
      const software = license + maintenance;
      const futTotal = futLabor + futErrCost + software;
      const savings = currTotal - futTotal;
      const timeReduction = inp.currentHours > 0 ? (1 - inp.futureHours / inp.currentHours) * 100 : 0;

      perProduct[id] = {
        cycles, currHrs, currLabor, currErrs, currErrCost, currTotal,
        futHrs, futLabor, futErrs, futErrCost, software, futTotal,
        license, impl, maintenance, savings, timeReduction,
      };

      tCurrCost += currTotal; tCurrHrs += currHrs; tCurrErrCost += currErrCost;
      tFutLabor += futLabor; tFutErrCost += futErrCost; tFutHrs += futHrs;
      tLicense += license; tImpl += impl; tMaint += maintenance;
    }

    const tSoftware = tLicense + tMaint;
    const tFutCost = tFutLabor + tFutErrCost + tSoftware;
    const tSavings = tCurrCost - tFutCost;
    const tInvest = tLicense + tImpl + sharedCosts.trainingCost + tMaint;
    const threeSav = (tSavings * 3) - tImpl - sharedCosts.trainingCost;
    const roi = tInvest > 0 ? (threeSav / tInvest) * 100 : 0;
    const payback = tSavings > 0 ? tInvest / (tSavings / 12) : 0;
    const hrsSaved = tCurrHrs - tFutHrs;
    const fte = hrsSaved / 2080;

    return {
      perProduct,
      t: {
        currCost: tCurrCost, currHrs: tCurrHrs, currErrCost: tCurrErrCost,
        futLabor: tFutLabor, futErrCost: tFutErrCost, futHrs: tFutHrs,
        software: tSoftware, futCost: tFutCost,
        license: tLicense, impl: tImpl, maint: tMaint, training: sharedCosts.trainingCost,
        invest: tInvest, savings: tSavings, threeSav, roi, payback, hrsSaved, fte,
      },
    };
  }, [selectedProducts, productInputs, solutionOverrides, scope, sharedCosts]);

  const hasProducts = selectedProducts.size > 0;

  /* ─── PDF Generation ─── */
  const handleDownload = () => {
    if (!downloadReady) { setShowLeadGate(true); return; }
    generatePDF();
  };

  const submitLeadAndDownload = async () => {
    setLeadError('');
    const domain = leadEmail.split('@')[1]?.toLowerCase();
    if (!leadEmail || !leadEmail.includes('@') || !domain) { setLeadError('Please enter a valid email address.'); return; }
    if (BLOCKED_DOMAINS.includes(domain)) { setLeadError('Please use a company email address.'); return; }
    try {
      await fetch(`${API}/api/leads/business-case`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: leadEmail, full_name: leadName, company: scope.clientName, products: [...selectedProducts], countries: scope.countries, source: 'business_case_builder' }),
      });
    } catch {}
    setDownloadReady(true);
    setShowLeadGate(false);
    generatePDF();
  };

  const generatePDF = () => {
    const t = calc.t;
    const productRows = [...selectedProducts].map(id => {
      const m = PRODUCT_MODELS[id];
      const p = calc.perProduct[id];
      return `
        <tr style="border-bottom:1px solid #e5e7eb;">
          <td style="padding:10px;font-weight:600;">${m.name}</td>
          <td style="padding:10px;text-align:right;">${p.cycles.toLocaleString()}</td>
          <td style="padding:10px;text-align:right;color:#dc2626;">${fmt(p.currTotal)}</td>
          <td style="padding:10px;text-align:right;color:#16a34a;">${fmt(p.futTotal)}</td>
          <td style="padding:10px;text-align:right;font-weight:600;color:#0066CC;">${fmt(p.savings)}</td>
        </tr>`;
    }).join('');

    const productDetails = [...selectedProducts].map(id => {
      const m = PRODUCT_MODELS[id];
      const p = calc.perProduct[id];
      const inp = productInputs[id] || m.defaults;
      return `
        <div style="page-break-inside:avoid;margin-bottom:24px;">
          <h3 style="color:#0066CC;border-bottom:2px solid #e5e7eb;padding-bottom:6px;">${m.name}</h3>
          <p style="color:#666;font-size:13px;margin-bottom:12px;">${m.desc}</p>
          <div style="display:flex;gap:20px;">
            <div style="flex:1;background:#fef2f2;padding:16px;border-radius:8px;border-left:4px solid #ef4444;">
              <div style="font-weight:700;color:#991b1b;margin-bottom:8px;">${m.currentLabel}</div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Cycles/year:</span><span>${p.cycles.toLocaleString()}</span></div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Hours/cycle:</span><span>${inp.currentHours}h</span></div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Total hours:</span><span>${Math.round(p.currHrs).toLocaleString()}h</span></div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Labor cost:</span><span>${fmt(p.currLabor)}</span></div>
              ${scope.includeErrorCosts ? `<div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Error cost:</span><span>${fmt(p.currErrCost)}</span></div>` : ''}
              <div style="display:flex;justify-content:space-between;padding:8px 0;border-top:2px solid #fca5a5;margin-top:8px;font-weight:700;"><span>Total:</span><span style="color:#dc2626;">${fmt(p.currTotal)}</span></div>
            </div>
            <div style="flex:1;background:#f0fdf4;padding:16px;border-radius:8px;border-left:4px solid #22c55e;">
              <div style="font-weight:700;color:#166534;margin-bottom:8px;">${m.futureLabel}</div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Cycles/year:</span><span>${p.cycles.toLocaleString()}</span></div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Hours/cycle:</span><span>${fmtHrs(inp.futureHours)}</span></div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Total hours:</span><span>${p.futHrs < 1 ? p.futHrs.toFixed(1) : Math.round(p.futHrs).toLocaleString()}h</span></div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Labor cost:</span><span>${fmt(p.futLabor)}</span></div>
              ${scope.includeErrorCosts ? `<div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Error cost:</span><span>${fmt(p.futErrCost)}</span></div>` : ''}
              <div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Software:</span><span>${fmt(p.software)}</span></div>
              <div style="display:flex;justify-content:space-between;padding:8px 0;border-top:2px solid #86efac;margin-top:8px;font-weight:700;"><span>Total:</span><span style="color:#16a34a;">${fmt(p.futTotal)}</span></div>
            </div>
          </div>
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Taxera Business Case${scope.clientName ? ' - ' + scope.clientName : ''}</title>
<style>@media print{body{margin:0}@page{margin:1.5cm}.no-print{display:none!important}}body{font-family:'Segoe UI',Arial,sans-serif;padding:40px;color:#333;line-height:1.6;max-width:1100px;margin:0 auto}h1{color:#0066CC;border-bottom:3px solid #0066CC;padding-bottom:10px}h2{color:#1e293b;margin-top:30px;border-bottom:2px solid #e5e7eb;padding-bottom:8px}table{width:100%;border-collapse:collapse}th{background:#f1f5f9;text-align:left;padding:10px;border-bottom:2px solid #e5e7eb}td{padding:10px;border-bottom:1px solid #e5e7eb}</style></head><body>
<button class="no-print" onclick="window.print()" style="background:#0066CC;color:white;padding:12px 24px;border:none;border-radius:8px;font-size:14px;cursor:pointer;margin-bottom:20px;font-weight:600;">Print / Save as PDF</button>
<h1>Business Case: Tax Compliance Automation</h1>
${scope.clientName ? `<p style="font-size:18px;color:#0066CC;font-weight:600;">Prepared for: ${scope.clientName}</p>` : ''}
<div style="text-align:right;color:#999;font-size:12px;">Generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} | Powered by Taxera Technologies</div>
<div style="background:#0f172a;color:white;padding:24px;border-radius:12px;margin:24px 0;display:flex;gap:24px;flex-wrap:wrap;">
  <div style="flex:1;min-width:140px;"><div style="font-size:12px;opacity:0.7;">Annual Savings</div><div style="font-size:28px;font-weight:700;">${fmt(t.savings)}</div></div>
  <div style="flex:1;min-width:140px;"><div style="font-size:12px;opacity:0.7;">3-Year ROI</div><div style="font-size:28px;font-weight:700;">${Math.round(t.roi)}%</div></div>
  <div style="flex:1;min-width:140px;"><div style="font-size:12px;opacity:0.7;">Payback Period</div><div style="font-size:28px;font-weight:700;">${t.payback.toFixed(1)} months</div></div>
  <div style="flex:1;min-width:140px;"><div style="font-size:12px;opacity:0.7;">FTE Capacity Freed</div><div style="font-size:28px;font-weight:700;">${t.fte.toFixed(1)}</div></div>
</div>
<h2>Scope</h2>
<table><tr><td><strong>Legal Entities:</strong></td><td>${scope.legalEntities}</td><td><strong>Countries:</strong></td><td>${scope.countries}</td><td><strong>Hourly Rate:</strong></td><td>${fmt(scope.hourlyRate)}</td></tr></table>
<h2>Product Summary</h2>
<table><thead><tr><th>Product</th><th style="text-align:right;">Cycles/yr</th><th style="text-align:right;">Current Cost</th><th style="text-align:right;">With Taxera</th><th style="text-align:right;">Net Savings</th></tr></thead><tbody>${productRows}
<tr style="background:#f0fdf4;font-weight:700;"><td style="padding:10px;">TOTAL</td><td></td><td style="padding:10px;text-align:right;color:#dc2626;">${fmt(t.currCost)}</td><td style="padding:10px;text-align:right;color:#16a34a;">${fmt(t.futCost)}</td><td style="padding:10px;text-align:right;color:#0066CC;">${fmt(t.savings)}</td></tr></tbody></table>
<h2>Detailed Product Breakdown</h2>
${productDetails}
<h2>Solution Investment</h2>
<table>
<tr><td>Annual License Fees</td><td style="text-align:right;">${fmt(t.license)}</td></tr>
<tr><td>Implementation (one-time)</td><td style="text-align:right;">${fmt(t.impl)}</td></tr>
<tr><td>Training (one-time)</td><td style="text-align:right;">${fmt(t.training)}</td></tr>
<tr><td>Annual Maintenance</td><td style="text-align:right;">${fmt(t.maint)}</td></tr>
<tr style="font-weight:700;border-top:2px solid #e5e7eb;"><td style="padding:10px;">Total Investment (Year 1)</td><td style="text-align:right;padding:10px;">${fmt(t.invest)}</td></tr>
</table>
<h2>Executive Summary</h2>
<div style="background:#eff6ff;padding:20px;border-radius:8px;border:2px solid #93c5fd;font-size:14px;line-height:1.8;">
<p>This business case demonstrates a compelling investment for automating tax compliance across ${scope.legalEntities} legal entities operating in ${scope.countries} countries, covering ${selectedProducts.size} Taxera product${selectedProducts.size !== 1 ? 's' : ''}.</p>
<p>Currently, the organization invests ${Math.round(t.currHrs).toLocaleString()} hours annually on manual compliance processes at a total cost of ${fmt(t.currCost)}. The proposed Taxera automation reduces this to ${Math.round(t.futHrs).toLocaleString()} hours, delivering annual savings of <strong>${fmt(t.savings)}</strong> with a payback period of just <strong>${t.payback.toFixed(1)} months</strong>.</p>
<p>With <strong>${t.fte.toFixed(1)} FTE</strong> capacity freed for higher-value strategic work and a <strong>${Math.round(t.roi)}% ROI</strong> over three years (${fmt(t.threeSav)} total value), this investment delivers immediate operational efficiency and long-term competitive advantage.</p>
</div>
<p style="margin-top:40px;padding-top:20px;border-top:2px solid #e5e7eb;font-size:11px;color:#999;text-align:center;">
Generated on ${new Date().toLocaleDateString('en-GB')} | Powered by Taxera Technologies | All figures are estimates based on provided inputs and can be adjusted.
</p></body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) w.onload = () => { setTimeout(() => w.print(), 500); };
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  /* ═══ RENDER ═══ */
  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="business-case-page">

        {/* ─── Header ─── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-taxera-blue/10 text-taxera-blue text-sm font-medium rounded-full mb-6">
            <FileText className="w-4 h-4" strokeWidth={1.5} />
            Business Case Builder
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-taxera-charcoal leading-tight mb-4">
            Build your case.<br />
            <span className="taxera-gradient-text">Win the budget.</span>
          </h1>
          <p className="text-base text-slate-600 max-w-2xl">
            Every number below is editable and transparent. Adjust assumptions to match your reality — then download a professional business case to share with your stakeholders.
          </p>
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
            <Lock className="w-3 h-3" />
            <span>Trusted by enterprise tax teams at leading multinationals including Amadeus, KUKA, and Voestalpine</span>
          </div>
        </motion.div>

        {/* ═══ SECTION 1: Scope ═══ */}
        <section className="bg-slate-50 rounded-2xl border-2 border-slate-200 p-6 mb-8" data-testid="scope-section">
          <h2 className="font-heading text-lg font-bold text-taxera-charcoal mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-taxera-blue" strokeWidth={1.5} /> Scope & Parameters
          </h2>
          <div className="mb-4">
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Client / Company Name</label>
            <input
              type="text" value={scope.clientName}
              onChange={(e) => updateScope('clientName', e.target.value)}
              placeholder="Enter company name"
              className="w-full max-w-md px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20"
              data-testid="input-client-name"
            />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <NumField label="Legal Entities" value={scope.legalEntities} onChange={(v) => updateScope('legalEntities', v)} min={1} testId="input-entities" />
            <NumField label="Countries of Operation" value={scope.countries} onChange={(v) => updateScope('countries', v)} min={1} testId="input-countries" />
            <NumField label="Hourly Rate" value={scope.hourlyRate} onChange={(v) => updateScope('hourlyRate', v)} min={1} prefix="€" hint="Fully loaded cost per hour for compliance staff" testId="input-hourly-rate" />
          </div>
          <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
            <button onClick={() => setScope(p => ({ ...p, includeErrorCosts: !p.includeErrorCosts }))} className={`w-10 h-6 rounded-full transition-colors relative ${scope.includeErrorCosts ? 'bg-taxera-teal' : 'bg-slate-300'}`} data-testid="toggle-error-costs">
              <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${scope.includeErrorCosts ? 'left-5' : 'left-1'}`} />
            </button>
            <div>
              <span className="text-sm font-semibold text-slate-700">Include Error / Non-Compliance Costs</span>
              <p className="text-2xs text-slate-400">Add cost of errors, discrepancies, and corrections to the analysis</p>
            </div>
          </div>
        </section>

        {/* ═══ SECTION 2: Product Selection ═══ */}
        <section className="mb-8" data-testid="product-selection">
          <h2 className="font-heading text-lg font-bold text-taxera-charcoal mb-4">Select Products</h2>
          <p className="text-sm text-slate-500 mb-4">Choose the Taxera products to include in your business case. Each product has its own transparent cost model below.</p>

          <div className="mb-4">
            <div className="text-2xs font-bold text-slate-400 uppercase tracking-wider mb-2">Core Compliance</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {CORE_PRODUCTS.map(p => (
                <button key={p.id} onClick={() => toggleProduct(p.id)}
                  className={`text-left p-3 rounded-xl border-2 transition-all text-sm ${selectedProducts.has(p.id) ? 'border-taxera-blue bg-taxera-blue/5' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  data-testid={`product-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className={`font-bold ${selectedProducts.has(p.id) ? 'text-taxera-blue' : 'text-slate-700'}`}>{p.name}</span>
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${selectedProducts.has(p.id) ? 'bg-taxera-blue border-taxera-blue' : 'border-slate-300'}`}>
                      {selectedProducts.has(p.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{p.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-2xs font-bold text-slate-400 uppercase tracking-wider mb-2">Reconciliation</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {RECON_PRODUCTS.map(p => (
                <button key={p.id} onClick={() => toggleProduct(p.id)}
                  className={`text-left p-3 rounded-xl border-2 transition-all text-sm ${selectedProducts.has(p.id) ? 'border-taxera-blue bg-taxera-blue/5' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  data-testid={`product-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className={`font-bold ${selectedProducts.has(p.id) ? 'text-taxera-blue' : 'text-slate-700'}`}>{p.name}</span>
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${selectedProducts.has(p.id) ? 'bg-taxera-blue border-taxera-blue' : 'border-slate-300'}`}>
                      {selectedProducts.has(p.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{p.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ SECTION 3: Per-Product Detail ═══ */}
        {hasProducts && (
          <section className="mb-8 space-y-4" data-testid="product-details">
            <h2 className="font-heading text-lg font-bold text-taxera-charcoal">Product Cost Models</h2>
            <p className="text-sm text-slate-500">Every number is editable. Adjust to match your reality — the business case updates in real-time.</p>

            {[...selectedProducts].map(id => {
              const m = PRODUCT_MODELS[id];
              const inp = productInputs[id] || m.defaults;
              const p = calc.perProduct[id];
              const isExpanded = expandedProducts.has(id);
              if (!p) return null;

              return (
                <div key={id} className="rounded-2xl border-2 border-slate-200 overflow-hidden bg-white" data-testid={`detail-${id}`}>
                  {/* Collapse header */}
                  <button onClick={() => toggleExpand(id)} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-taxera-blue/10 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-taxera-blue" />
                      </div>
                      <div>
                        <div className="font-heading text-sm font-bold text-taxera-charcoal">{m.name}</div>
                        <div className="text-xs text-slate-400">{p.cycles.toLocaleString()} {m.cycleLabel} / year &middot; {m.cycleExplain(scope.legalEntities, scope.countries)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <div className="text-2xs text-slate-400">Savings</div>
                        <div className={`font-heading text-sm font-bold ${p.savings > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(p.savings)}/yr</div>
                      </div>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                        <div className="px-5 pb-5 border-t border-slate-100">
                          {/* As-Is vs To-Be */}
                          <div className="grid md:grid-cols-2 gap-4 mt-4">
                            {/* CURRENT / AS-IS */}
                            <div className="bg-red-50/70 p-5 rounded-xl border border-red-200">
                              <h4 className="font-bold text-sm text-red-900 mb-3">{m.currentLabel}</h4>
                              <NumField label="Hours per cycle" value={inp.currentHours} onChange={(v) => updateProductInput(id, 'currentHours', v)} step={0.5} min={0} testId={`${id}-curr-hours`} hint={`Tip: 1 minute = 0.017 hours`} />
                              <div className="mt-3 p-3 bg-white/70 rounded-lg text-sm space-y-1">
                                <div className="flex justify-between"><span className="text-slate-600">Total hours/year:</span><span className="font-semibold">{Math.round(p.currHrs).toLocaleString()}h</span></div>
                                <div className="flex justify-between"><span className="text-slate-600">Labor cost:</span><span className="font-semibold">{fmt(p.currLabor)}</span></div>
                                {scope.includeErrorCosts && <div className="flex justify-between"><span className="text-slate-600">Error cost ({Math.round(p.currErrs)} errors):</span><span className="font-semibold">{fmt(p.currErrCost)}</span></div>}
                                <div className="flex justify-between pt-2 border-t border-red-200 font-bold"><span>Annual total:</span><span className="text-red-600">{fmt(p.currTotal)}</span></div>
                              </div>
                            </div>

                            {/* FUTURE / TO-BE */}
                            <div className="bg-emerald-50/70 p-5 rounded-xl border border-emerald-200">
                              <h4 className="font-bold text-sm text-emerald-900 mb-3">{m.futureLabel}</h4>
                              <NumField label="Hours per cycle" value={inp.futureHours} onChange={(v) => updateProductInput(id, 'futureHours', v)} step={0.001} min={0} testId={`${id}-fut-hours`} hint={`Tip: 1 minute = 0.017 hours`} />
                              <div className="mt-3 p-3 bg-white/70 rounded-lg text-sm space-y-1">
                                <div className="flex justify-between"><span className="text-slate-600">Total hours/year:</span><span className="font-semibold">{p.futHrs < 1 ? p.futHrs.toFixed(1) : Math.round(p.futHrs).toLocaleString()}h</span></div>
                                <div className="flex justify-between"><span className="text-slate-600">Labor cost:</span><span className="font-semibold">{fmt(p.futLabor)}</span></div>
                                {scope.includeErrorCosts && <div className="flex justify-between"><span className="text-slate-600">Error cost ({Math.round(p.futErrs)} errors):</span><span className="font-semibold">{fmt(p.futErrCost)}</span></div>}
                                <div className="flex justify-between"><span className="text-slate-600">Software (license + maint.):</span><span className="font-semibold">{fmt(p.software)}</span></div>
                                <div className="flex justify-between pt-2 border-t border-emerald-200 font-bold"><span>Annual total:</span><span className="text-emerald-600">{fmt(p.futTotal)}</span></div>
                              </div>
                            </div>
                          </div>

                          {/* Error parameters */}
                          {scope.includeErrorCosts && (
                            <div className="mt-4 bg-amber-50/70 p-4 rounded-xl border border-amber-200">
                              <h4 className="font-bold text-xs text-amber-900 mb-3">Error / Non-Compliance Parameters</h4>
                              <div className="grid sm:grid-cols-3 gap-3">
                                <NumField label="Current Error Rate" value={inp.errorRate} onChange={(v) => updateProductInput(id, 'errorRate', v)} step={0.5} min={0} suffix="%" testId={`${id}-err-rate`} />
                                <NumField label="Cost per Error" value={inp.costPerError} onChange={(v) => updateProductInput(id, 'costPerError', v)} step={100} min={0} prefix="€" testId={`${id}-err-cost`} />
                                <NumField label="Error Reduction with Taxera" value={inp.errorReduction} onChange={(v) => updateProductInput(id, 'errorReduction', v)} step={1} min={0} suffix="%" testId={`${id}-err-reduction`} />
                              </div>
                            </div>
                          )}

                          {/* Solution costs for this product */}
                          <div className="mt-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <h4 className="font-bold text-xs text-slate-600 mb-3">Solution Costs — {m.name}</h4>
                            <div className="grid sm:grid-cols-3 gap-3">
                              <NumField
                                label="Annual License"
                                value={solutionOverrides[id]?.license ?? (m.solution.license + m.solution.perCountry * scope.countries)}
                                onChange={(v) => updateSolutionOverride(id, 'license', v)}
                                step={1000} min={0} prefix="€"
                                hint={`Default: €${m.solution.license.toLocaleString()} base + €${m.solution.perCountry.toLocaleString()} × ${scope.countries} countries`}
                                testId={`${id}-license`}
                              />
                              <NumField
                                label="Implementation (one-time)"
                                value={solutionOverrides[id]?.impl ?? m.solution.impl}
                                onChange={(v) => updateSolutionOverride(id, 'impl', v)}
                                step={1000} min={0} prefix="€"
                                testId={`${id}-impl`}
                              />
                              <NumField
                                label="Annual Maintenance"
                                value={solutionOverrides[id]?.maintenance ?? Math.round((solutionOverrides[id]?.license ?? (m.solution.license + m.solution.perCountry * scope.countries)) * 0.2)}
                                onChange={(v) => updateSolutionOverride(id, 'maintenance', v)}
                                step={500} min={0} prefix="€"
                                hint="Default: 20% of license"
                                testId={`${id}-maint`}
                              />
                            </div>
                          </div>

                          {/* Time reduction badge */}
                          <div className="mt-3 flex items-center gap-2 text-xs text-taxera-blue font-semibold">
                            <Clock className="w-3.5 h-3.5" /> Time reduction: {inp.currentHours}h → {fmtHrs(inp.futureHours)} per cycle ({Math.round(p.timeReduction)}% reduction)
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}

            {/* Shared costs */}
            <div className="bg-slate-50 rounded-2xl border-2 border-slate-200 p-5">
              <h3 className="font-bold text-sm text-slate-700 mb-3">Shared Costs</h3>
              <div className="max-w-xs">
                <NumField label="Training (one-time, all products)" value={sharedCosts.trainingCost} onChange={(v) => setSharedCosts(p => ({ ...p, trainingCost: v }))} step={1000} min={0} prefix="€" testId="shared-training" />
              </div>
            </div>
          </section>
        )}

        {/* ═══ SECTION 4: Results ═══ */}
        {hasProducts && (
          <>
            {/* KPI Banner */}
            <section className="bg-slate-900 rounded-2xl p-8 mb-6" data-testid="results-kpis">
              <h2 className="text-sm font-bold text-taxera-teal uppercase tracking-wider mb-6 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Results
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white/10 p-4 rounded-xl" data-testid="kpi-savings">
                  <div className="text-xs text-slate-400 mb-1">Annual Savings</div>
                  <div className={`font-heading text-3xl font-bold ${calc.t.savings > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmt(calc.t.savings)}</div>
                </div>
                <div className="bg-white/10 p-4 rounded-xl" data-testid="kpi-roi">
                  <div className="text-xs text-slate-400 mb-1">3-Year ROI</div>
                  <div className="font-heading text-3xl font-bold text-white">{Math.round(calc.t.roi)}%</div>
                </div>
                <div className="bg-white/10 p-4 rounded-xl" data-testid="kpi-payback">
                  <div className="text-xs text-slate-400 mb-1">Payback Period</div>
                  <div className="font-heading text-3xl font-bold text-white">{calc.t.payback.toFixed(1)} <span className="text-lg">months</span></div>
                </div>
                <div className="bg-white/10 p-4 rounded-xl" data-testid="kpi-fte">
                  <div className="text-xs text-slate-400 mb-1">FTE Capacity Freed</div>
                  <div className="font-heading text-3xl font-bold text-white">{calc.t.fte.toFixed(1)}</div>
                  <div className="text-2xs text-slate-500 mt-0.5">{Math.round(calc.t.hrsSaved).toLocaleString()} hours saved/year</div>
                </div>
              </div>
            </section>

            {/* Cost Comparison 3-column */}
            <section className="grid md:grid-cols-3 gap-4 mb-6" data-testid="cost-comparison">
              {/* Current */}
              <div className="bg-red-50 p-5 rounded-2xl border border-red-200">
                <h3 className="font-bold text-sm text-slate-800 mb-3">Current Annual Costs</h3>
                <div className="space-y-1 text-sm">
                  <MetricRow label="Labor" sublabel={`${Math.round(calc.t.currHrs).toLocaleString()} hours × €${scope.hourlyRate}/hr`} value={fmt(calc.t.currCost - calc.t.currErrCost)} />
                  {scope.includeErrorCosts && <MetricRow label="Errors / Non-Compliance" value={fmt(calc.t.currErrCost)} />}
                  <div className="border-t-2 border-red-200 pt-2 mt-2">
                    <MetricRow label="Total" value={fmt(calc.t.currCost)} bold color="text-red-600" />
                  </div>
                </div>
              </div>

              {/* Future */}
              <div className="bg-emerald-50 p-5 rounded-2xl border border-emerald-200">
                <h3 className="font-bold text-sm text-slate-800 mb-3">With Taxera (Annual)</h3>
                <div className="space-y-1 text-sm">
                  <MetricRow label="Labor" sublabel={`${Math.round(calc.t.futHrs).toLocaleString()} hours × €${scope.hourlyRate}/hr`} value={fmt(calc.t.futLabor)} />
                  {scope.includeErrorCosts && <MetricRow label="Residual Errors" value={fmt(calc.t.futErrCost)} />}
                  <MetricRow label="Software" sublabel="License + maintenance" value={fmt(calc.t.software)} />
                  <div className="border-t-2 border-emerald-200 pt-2 mt-2">
                    <MetricRow label="Total" value={fmt(calc.t.futCost)} bold color="text-emerald-600" />
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-blue-50 p-5 rounded-2xl border border-blue-200">
                <h3 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-taxera-blue" /> Benefits
                </h3>
                <div className="space-y-1 text-sm">
                  <MetricRow label="Hours Saved / Year" value={`${Math.round(calc.t.hrsSaved).toLocaleString()}h`} />
                  <MetricRow label="FTE Capacity Freed" value={calc.t.fte.toFixed(1)} />
                  <MetricRow label="3-Year Total Value" value={fmt(calc.t.threeSav)} />
                  <div className="border-t-2 border-blue-200 pt-2 mt-2">
                    <MetricRow label="Net Annual Savings" value={fmt(calc.t.savings)} bold color="text-blue-600" />
                  </div>
                </div>
              </div>
            </section>

            {/* Investment breakdown */}
            <section className="bg-slate-50 rounded-2xl border border-slate-200 p-5 mb-6" data-testid="investment-summary">
              <h3 className="font-bold text-sm text-slate-700 mb-3">Solution Investment Summary</h3>
              <div className="space-y-1 text-sm max-w-lg">
                <MetricRow label="Annual License Fees" sublabel={`${selectedProducts.size} products across ${scope.countries} countries`} value={fmt(calc.t.license)} />
                <MetricRow label="Implementation (one-time)" value={fmt(calc.t.impl)} />
                <MetricRow label="Training (one-time)" value={fmt(calc.t.training)} />
                <MetricRow label="Annual Maintenance" value={fmt(calc.t.maint)} />
                <div className="border-t-2 border-slate-300 pt-2 mt-2">
                  <MetricRow label="Year 1 Total Investment" value={fmt(calc.t.invest)} bold />
                </div>
              </div>
            </section>

            {/* Executive Summary */}
            <section className="bg-blue-50 rounded-2xl border-2 border-blue-200 p-6 mb-8" data-testid="executive-summary">
              <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-3">Executive Summary</h3>
              <div className="text-sm leading-relaxed space-y-3 text-slate-700">
                <p>
                  This business case demonstrates a compelling investment for automating tax compliance across{' '}
                  <strong>{scope.legalEntities} legal entities</strong> operating in <strong>{scope.countries} countries</strong>,
                  covering <strong>{selectedProducts.size} Taxera product{selectedProducts.size !== 1 ? 's' : ''}</strong>
                  {scope.clientName ? ` for ${scope.clientName}` : ''}.
                </p>
                <p>
                  Currently, the organization invests <strong>{Math.round(calc.t.currHrs).toLocaleString()} hours</strong> annually
                  on manual compliance processes at a total cost of <strong>{fmt(calc.t.currCost)}</strong>.
                  The proposed automation reduces this to <strong>{Math.round(calc.t.futHrs).toLocaleString()} hours</strong>,
                  delivering annual savings of <strong>{fmt(calc.t.savings)}</strong> with a payback period of
                  just <strong>{calc.t.payback.toFixed(1)} months</strong>.
                </p>
                <p>
                  With <strong>{calc.t.fte.toFixed(1)} FTE</strong> capacity freed for higher-value strategic work and
                  a <strong>{Math.round(calc.t.roi)}% ROI</strong> over three years ({fmt(calc.t.threeSav)} total value),
                  this investment delivers immediate operational efficiency and long-term competitive advantage.
                  Beyond the quantitative benefits, the solution provides real-time compliance visibility,
                  faster month-end close cycles, improved audit readiness, elimination of manual spreadsheet errors,
                  and scalability to handle growth without proportional headcount increases.
                </p>
              </div>
            </section>

            {/* Download */}
            <div className="text-center mb-8">
              <button
                onClick={handleDownload}
                className="group inline-flex items-center gap-3 px-10 py-5 taxera-gradient text-white rounded-full font-semibold text-lg hover:shadow-2xl transition-all hover:scale-105"
                data-testid="download-pdf"
              >
                <Download className="w-5 h-5" />
                Download Business Case PDF
              </button>
              <p className="text-2xs text-slate-400 mt-3">
                All figures are estimates based on your inputs and can be adjusted. Opens a print-ready document.
              </p>
            </div>
          </>
        )}

        {/* Empty state */}
        {!hasProducts && (
          <div className="text-center py-16 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-sm">Select one or more products above to start building your business case.</p>
          </div>
        )}

        {/* ═══ Lead Gate Modal ═══ */}
        <AnimatePresence>
          {showLeadGate && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
              onClick={() => setShowLeadGate(false)}>
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}
                data-testid="lead-gate-modal">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-taxera-blue/10 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-taxera-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-taxera-charcoal">Download your business case</h3>
                    <p className="text-xs text-slate-500">Enter your details to get the PDF</p>
                  </div>
                </div>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Full Name</label>
                    <input type="text" value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Jane Smith"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20"
                      data-testid="lead-name" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Work Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="email" value={leadEmail} onChange={(e) => { setLeadEmail(e.target.value); setLeadError(''); }} placeholder="jane@company.com"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20"
                        data-testid="lead-email" />
                    </div>
                    {leadError && <p className="text-xs text-red-500 mt-1" data-testid="lead-error">{leadError}</p>}
                  </div>
                </div>
                <button onClick={submitLeadAndDownload}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 taxera-gradient text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                  data-testid="lead-submit">
                  <Download className="w-4 h-4" /> Download Business Case
                </button>
                <p className="text-2xs text-slate-400 text-center mt-3">By downloading, you agree to Taxera's privacy policy.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </PageLayout>
  );
};

export default BusinessCasePage;
